import type { Curriculum } from '@/content/curriculum';
import type { LearningObjective } from '@/content/schema';
import type { GameId, LevelId } from '@/domain/ids';
import type { MasteryRecord } from '@/domain/records';
import { daysBetween } from '@/lib/time';
import { isDue, isSecure } from './model';

export type RecommendationKind =
  'review' | 'prerequisite' | 'easier' | 'practice' | 'next' | 'start';

export interface Recommendation {
  kind: RecommendationKind;
  /** Objective to practise now. */
  objectiveId: string;
  /** Objective that triggered the recommendation (e.g. the one that needs a prerequisite). */
  becauseOfId?: string;
  reason: { code: RecommendationKind; params: Record<string, string | number> };
  game: GameId;
  easier: boolean;
  priority: number;
}

const PRIORITY: Record<RecommendationKind, number> = {
  review: 1,
  prerequisite: 2,
  easier: 3,
  practice: 4,
  next: 5,
  start: 6,
};

function suggestedGame(objective: LearningObjective, preferred: GameId[]): GameId {
  return preferred.find((g) => objective.games.includes(g)) ?? objective.games[0] ?? 'quick-quiz';
}

export interface RecommendInput {
  curriculum: Curriculum;
  levelId: LevelId;
  variantId: string;
  mastery: Map<string, MasteryRecord>;
  now: Date;
  subjectId?: string;
  limit?: number;
}

/**
 * Rules-based, explainable recommendations. Each one carries a reason code and
 * the numbers behind it so the interface can say *why* it is suggested.
 */
export function recommend(input: RecommendInput): Recommendation[] {
  const { curriculum, levelId, variantId, mastery, now } = input;
  const subjects = input.subjectId
    ? curriculum.subjectsFor(levelId).filter((s) => s.id === input.subjectId)
    : curriculum.subjectsFor(levelId);
  const out: Recommendation[] = [];
  const levelObjectiveIds = new Set(
    curriculum.objectivesForLevel(levelId, variantId).map((o) => o.id),
  );

  for (const subject of subjects) {
    const objectives = curriculum.objectivesFor(subject.id, levelId, variantId);
    let nextAdded = false;
    const anyStarted = objectives.some((o) => (mastery.get(o.id)?.attempts ?? 0) > 0);

    for (const objective of objectives) {
      const record = mastery.get(objective.id);
      const status = record?.status ?? 'notStarted';
      const add = (rec: Omit<Recommendation, 'priority'>) =>
        out.push({ ...rec, priority: PRIORITY[rec.kind] });

      if (record && isDue(record, now)) {
        add({
          kind: 'review',
          objectiveId: objective.id,
          reason: {
            code: 'review',
            params: { days: record.lastPracticedAt ? daysBetween(record.lastPracticedAt, now) : 0 },
          },
          game: suggestedGame(objective, ['quick-quiz', 'find-correct']),
          easier: false,
        });
        continue;
      }

      if (status === 'needsPractice') {
        const weak = objective.prerequisites
          .map((id) => curriculum.objective(id))
          .find((p): p is LearningObjective => !!p && !isSecure(mastery.get(p.id)?.status));
        if (weak) {
          add({
            kind: 'prerequisite',
            objectiveId: weak.id,
            becauseOfId: objective.id,
            reason: {
              code: 'prerequisite',
              params: { objective: objective.id, prerequisite: weak.id },
            },
            game: suggestedGame(weak, ['find-correct', 'quick-quiz']),
            easier: true,
          });
        }
        add({
          kind: 'easier',
          objectiveId: objective.id,
          reason: {
            code: 'easier',
            params: {
              percent: Math.round((record?.recentScore ?? 0) * 100),
              ...(record?.topMistake ? { mistake: record.topMistake } : {}),
            },
          },
          game: suggestedGame(objective, ['find-correct', 'quick-quiz']),
          easier: true,
        });
        continue;
      }

      if (status === 'learning' || status === 'progressing') {
        add({
          kind: 'practice',
          objectiveId: objective.id,
          reason: {
            code: 'practice',
            params: {
              percent: Math.round((record?.recentScore ?? 0) * 100),
              answered: record?.attempts ?? 0,
            },
          },
          game: suggestedGame(objective, ['quick-quiz']),
          easier: false,
        });
        continue;
      }

      if (status === 'notStarted' && !nextAdded) {
        // Earlier-year prerequisites never block: the child may have learned them at school.
        const ready = objective.prerequisites.every(
          (id) => !levelObjectiveIds.has(id) || isSecure(mastery.get(id)?.status),
        );
        if (ready) {
          nextAdded = true;
          add({
            kind: anyStarted ? 'next' : 'start',
            objectiveId: objective.id,
            reason: { code: anyStarted ? 'next' : 'start', params: { subject: subject.id } },
            game: suggestedGame(objective, ['quick-quiz', 'find-correct']),
            easier: false,
          });
        }
      }
    }
  }

  const seen = new Set<string>();
  const order = (id: string) => curriculum.objective(id)?.order ?? 0;
  return out
    .sort((a, b) => a.priority - b.priority || order(a.objectiveId) - order(b.objectiveId))
    .filter((r) => (seen.has(r.objectiveId) ? false : (seen.add(r.objectiveId), true)))
    .slice(0, input.limit ?? 20);
}
